// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { BundleSortProperty } from '@/bundle/api/types';
import { BundleTableRow } from '@/bundle/components/bundle-table-row';
import { useBundles } from '@/bundle/hooks/use-bundles';
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { useTableSort } from '@/hooks/use-table-sort';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useEffect } from 'react';

const PAGE_SIZE = 50;

interface Props {
  searchQuery?: string;
  includeArchived?: boolean;
  sort: Sort<BundleSortProperty>;
  onSort: (property: BundleSortProperty) => void;
  page?: number;
  onPageChange: (page: number) => void;
  onAddBundle: () => void;
}

/**
 * The bundles table with sortable headers, member previews, and pagination.
 * Fetches data with suspense; render inside a Suspense boundary.
 */
export function BundlesTable(props: Props) {
  const {
    searchQuery,
    includeArchived,
    sort,
    onSort,
    page = 1,
    onPageChange,
    onAddBundle,
  } = props;

  const { bundles, total } = useBundles({
    query: searchQuery,
    archived: includeArchived,
    sort: sort.property,
    direction: sort.direction,
    page,
    limit: PAGE_SIZE,
  });

  // Handle page overrun (e.g., if filters reduce total bundles)
  useEffect(() => {
    if (bundles.length === 0 && page > 1) {
      onPageChange(1);
    }
  }, [bundles.length, page, onPageChange]);

  const { getSortDirection, getLabelDirection, isLabelActive } =
    useTableSort(sort);

  const createSortHandler = (column: BundleSortProperty) => () => {
    onSort(column);
  };

  if (bundles.length === 0) {
    if (searchQuery) {
      return (
        <Paper variant="outlined">
          <EmptyState
            title="No bundles match your search"
            message="Try adjusting your search or show archived bundles from the gear menu"
          />
        </Paper>
      );
    }
    return (
      <Paper variant="outlined">
        <EmptyState
          title="No bundles yet"
          message="Bundles let you save a set of items you frequently order together and add them all to a document at once"
        >
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={onAddBundle}
          >
            Add bundle
          </Button>
        </EmptyState>
      </Paper>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="medium" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {/* Name column */}
              <TableCell
                sortDirection={getSortDirection('name')}
                sx={{ width: '16%', minWidth: 160 }}
              >
                <TableSortLabel
                  active={isLabelActive('name')}
                  direction={getLabelDirection('name')}
                  onClick={createSortHandler('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              {/* Description column */}
              <TableCell sx={{ width: '24%', minWidth: 220 }}>
                Description
              </TableCell>
              {/* Items column */}
              <TableCell sx={{ minWidth: 240 }}>Items</TableCell>
              {/* Last updated column */}
              <TableCell
                sortDirection={getSortDirection('lastUpdatedAt')}
                sx={{ width: 180 }}
              >
                <TableSortLabel
                  active={isLabelActive('lastUpdatedAt')}
                  direction={getLabelDirection('lastUpdatedAt')}
                  onClick={createSortHandler('lastUpdatedAt')}
                >
                  Last updated
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bundles.map((bundle) => (
              <BundleTableRow key={bundle.id} bundle={bundle} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          onPageChange,
        }}
        totalCount={total}
        entityOptions={{ singular: 'bundle', includeCount: false }}
      />
    </>
  );
}
